import { useEffect, useState } from "react";
import {
  BookOpen,
  Check,
  Clock,
  Compass,
  Globe,
  Menu,
  Mic,
  Sparkles,
  Star,
  User,
  X,
} from "lucide-react";
import { CAL_URL, LANG_LABELS, LANGS, TALLY_URL, translations, type Lang } from "./i18n";

const PROCESS_ICONS = [Compass, BookOpen, Mic, Sparkles];
const USE_CASE_ICONS = [User, Star, Sparkles];

function useLanguage() {
  const [lang, setLang] = useState<Lang>(() => {
    if (typeof window === "undefined") return "en";
    const saved = window.localStorage.getItem("speaksprout-lang");
    if (saved === "en" || saved === "fr" || saved === "de") return saved;
    const nav = window.navigator.language.slice(0, 2);
    if (nav === "fr") return "fr";
    if (nav === "de") return "de";
    return "en";
  });

  useEffect(() => {
    window.localStorage.setItem("speaksprout-lang", lang);
    document.documentElement.lang = lang;
  }, [lang]);

  return { lang, setLang };
}

function Logo({ dark = false }: { dark?: boolean }) {
  return (
    <a href="#home" className="flex items-center gap-2" aria-label="SpeakSprout">
      <img src="/leaf-mark.svg" alt="" aria-hidden="true" className="h-7 w-7 md:h-8 md:w-8" />
      <span
        className={`text-lg font-extrabold tracking-tight md:text-xl ${
          dark ? "text-mist" : "text-ink"
        }`}
      >
        SpeakSprout
      </span>
    </a>
  );
}

function LanguageSwitcher({
  lang,
  setLang,
  dark = false,
}: {
  lang: Lang;
  setLang: (l: Lang) => void;
  dark?: boolean;
}) {
  return (
    <div
      className={`flex items-center gap-1 rounded-full border p-0.5 ${
        dark ? "border-white/15 bg-white/5" : "border-ink/15 bg-white"
      }`}
    >
      <Globe size={15} className={dark ? "text-white/70" : "text-ink/60"} aria-hidden="true" />
      {LANGS.map((l) => (
        <button
          key={l}
          type="button"
          onClick={() => setLang(l)}
          aria-pressed={lang === l}
          className={`rounded-full px-2.5 py-1 text-xs font-semibold transition-colors ${
            lang === l
              ? "bg-lime text-ink"
              : dark
                ? "text-white/70 hover:text-white"
                : "text-ink/60 hover:text-ink"
          }`}
        >
          {LANG_LABELS[l]}
        </button>
      ))}
    </div>
  );
}

function Nav({ lang, setLang }: { lang: Lang; setLang: (l: Lang) => void }) {
  const [open, setOpen] = useState(false);
  const t = translations[lang];

  const links = [
    { href: "#home", label: t.nav.home },
    { href: "#process", label: t.nav.about },
    { href: "#use-cases", label: t.nav.solutions },
    { href: "#wall-of-fame", label: t.nav.clients },
    { href: "#contacts", label: t.nav.contacts },
  ];

  return (
    <header className="sticky top-0 z-50 border-b border-ink/10 bg-mist/90 backdrop-blur-md">
      <nav className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3 md:px-6">
        <Logo />
        <div className="hidden items-center gap-6 lg:flex">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              className="text-sm font-medium text-ink/70 transition-colors hover:text-ink"
            >
              {l.label}
            </a>
          ))}
        </div>
        <div className="flex items-center gap-3">
          <LanguageSwitcher lang={lang} setLang={setLang} />
          <a
            href={TALLY_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="hidden rounded-full bg-ink px-5 py-2.5 text-sm font-semibold text-mist transition-colors hover:bg-ink/90 md:inline-block"
            data-testid="nav-cta"
          >
            {t.nav.cta}
          </a>
          <button
            type="button"
            className="lg:hidden"
            aria-label={open ? "Close menu" : "Open menu"}
            aria-expanded={open}
            onClick={() => setOpen((v) => !v)}
            data-testid="burger"
          >
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </nav>
      {open && (
        <div
          className="border-t border-ink/10 bg-mist px-4 py-4 lg:hidden"
          data-testid="mobile-menu"
        >
          <div className="flex flex-col gap-1">
            {links.map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={() => setOpen(false)}
                className="rounded-lg px-3 py-3 text-base font-medium text-ink/80 transition-colors hover:bg-sky/40"
              >
                {l.label}
              </a>
            ))}
            <a
              href={TALLY_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setOpen(false)}
              className="mt-2 rounded-full bg-ink px-5 py-3 text-center text-sm font-semibold text-mist"
            >
              {t.nav.cta}
            </a>
          </div>
        </div>
      )}
    </header>
  );
}

function Hero({ lang }: { lang: Lang }) {
  const t = translations[lang];
  const [minutes, setMinutes] = useState(15);

  return (
    <section id="home" className="mx-auto max-w-7xl px-4 pt-12 pb-16 md:px-6 md:pt-20 md:pb-24">
      <div className="mx-auto max-w-3xl text-center">
        <h1 className="text-3xl font-extrabold leading-tight tracking-tight text-ink sm:text-4xl md:text-5xl lg:text-6xl">
          {t.hero.h1}
        </h1>
        <p className="mx-auto mt-5 max-w-2xl text-base text-ink/70 md:text-lg">{t.hero.sub}</p>
      </div>

      <div className="mx-auto mt-10 max-w-xl rounded-3xl border border-ink/10 bg-white p-6 shadow-sm md:mt-12 md:p-8">
        <div className="flex items-center justify-between">
          <label htmlFor="sprint-length" className="text-sm font-semibold text-ink/70">
            {t.hero.sliderLabel}
          </label>
          <span className="text-sm font-bold text-ink" data-testid="sprint-value">
            {minutes} min
          </span>
        </div>
        <input
          id="sprint-length"
          type="range"
          min={5}
          max={30}
          step={5}
          value={minutes}
          onChange={(e) => setMinutes(Number(e.target.value))}
          className="lime-range mt-3 w-full"
          aria-valuemin={5}
          aria-valuemax={30}
          aria-valuenow={minutes}
        />
        <div className="mt-1 flex justify-between text-xs text-ink/40">
          <span>5</span>
          <span>30</span>
        </div>
        <a
          href={TALLY_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-6 block w-full rounded-full bg-ink px-6 py-4 text-center text-base font-semibold text-mist transition-colors hover:bg-ink/90"
          data-testid="hero-cta"
        >
          {t.hero.cta}
        </a>
      </div>
    </section>
  );
}

function Process({ lang }: { lang: Lang }) {
  const t = translations[lang];
  return (
    <section id="process" className="bg-sky/40 py-16 md:py-24">
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        <h2 className="text-center text-2xl font-bold text-ink md:text-4xl">{t.process.title}</h2>
        <div className="mt-10 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4 lg:gap-6">
          {t.process.steps.map((step, i) => {
            const Icon = PROCESS_ICONS[i];
            return (
              <div
                key={step.title}
                className="rounded-2xl border border-ink/10 bg-white p-6 shadow-sm"
                data-testid="process-card"
              >
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-lime/20">
                  <Icon size={24} className="text-ink" aria-hidden="true" />
                </div>
                <div className="mt-4 text-xs font-bold text-ink/40">
                  {String(i + 1).padStart(2, "0")}
                </div>
                <h3 className="mt-1 text-lg font-bold text-ink">{step.title}</h3>
                <p className="mt-1 text-sm text-ink/60">{step.desc}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function Proof({ lang }: { lang: Lang }) {
  const t = translations[lang];
  return (
    <section id="proof" className="py-16 md:py-24">
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        <h2 className="text-center text-2xl font-bold text-ink md:text-4xl">{t.proof.title}</h2>
        <div className="mt-10 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4 lg:gap-6">
          {t.proof.cards.map((card) => (
            <div
              key={card.title}
              className="rounded-2xl bg-ink p-6 text-white"
              data-testid="proof-card"
            >
              <div className="flex items-center gap-2">
                <span className="flex h-6 w-6 items-center justify-center rounded-full bg-lime">
                  <Check size={14} className="text-ink" aria-hidden="true" />
                </span>
                <span className="text-sm font-semibold text-lime">{card.time}</span>
              </div>
              <h3 className="mt-4 text-lg font-bold leading-snug">{card.title}</h3>
              <p className="mt-1 text-xs text-white/40">{card.date}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function UseCases({ lang }: { lang: Lang }) {
  const t = translations[lang];
  return (
    <section id="use-cases" className="bg-sky/40 py-16 md:py-24">
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        <h2 className="text-center text-2xl font-bold text-ink md:text-4xl">{t.useCases.title}</h2>
        <div className="mt-10 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 lg:gap-6">
          {t.useCases.cards.map((card, i) => {
            const Icon = USE_CASE_ICONS[i];
            return (
              <div
                key={card.title}
                className="rounded-2xl border border-ink/10 bg-white p-6 shadow-sm md:p-8"
                data-testid="use-case-card"
              >
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-amber/20">
                  <Icon size={24} className="text-ink" aria-hidden="true" />
                </div>
                <h3 className="mt-4 text-xl font-bold text-ink">{card.title}</h3>
                <p className="mt-2 text-sm text-ink/60 md:text-base">{card.desc}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function Founders({ lang }: { lang: Lang }) {
  const t = translations[lang];
  const photos = ["founder-cyril.png", "founder-polina.png"];
  return (
    <section id="founders" className="py-16 md:py-24">
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        <h2 className="text-center text-2xl font-bold text-ink md:text-4xl">{t.founders.title}</h2>
        <div className="mx-auto mt-10 grid max-w-3xl grid-cols-1 gap-6 sm:grid-cols-2">
          {t.founders.people.map((person, i) => (
            <div key={person.name} className="flex flex-col items-center text-center">
              <div className="relative h-32 w-32 md:h-40 md:w-40">
                <img
                  src="/founders-bg.svg"
                  alt=""
                  aria-hidden="true"
                  className="absolute inset-0 h-full w-full"
                />
                <img
                  src={`/${photos[i]}`}
                  alt={person.name}
                  className="absolute inset-0 m-auto h-28 w-28 rounded-full object-cover md:h-32 md:w-32"
                />
              </div>
              <h3 className="mt-4 text-lg font-bold text-ink">{person.name}</h3>
              <p className="text-sm font-semibold text-ink/50">{person.role}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function WallOfFame({ lang }: { lang: Lang }) {
  const t = translations[lang];
  return (
    <section id="wall-of-fame" className="bg-ink py-16 md:py-24">
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        <h2 className="text-center text-2xl font-bold text-white md:text-4xl">
          {t.wallOfFame.title}
        </h2>
        <p className="mx-auto mt-3 max-w-xl text-center text-sm text-white/50 md:text-base">
          {t.wallOfFame.subtitle}
        </p>
        <div className="mt-10 flex flex-wrap justify-center gap-3">
          {t.wallOfFame.chips.map((chip) => (
            <span
              key={chip.topic}
              className="flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-2 text-sm text-white"
              data-testid="wof-chip"
            >
              <Star size={15} className="text-amber" aria-hidden="true" />
              <span className="font-medium">{chip.topic}</span>
              <span className="text-white/40">{chip.time}</span>
            </span>
          ))}
        </div>
      </div>
    </section>
  );
}

function Footer({ lang }: { lang: Lang }) {
  const t = translations[lang];
  return (
    <footer id="contacts" className="bg-ink py-14 md:py-20">
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        <div className="flex flex-col items-center gap-8 text-center">
          <Logo dark />
          <a
            href={CAL_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 rounded-full bg-lime px-6 py-3.5 text-sm font-semibold text-ink transition-colors hover:bg-lime/90"
            data-testid="book-call"
          >
            <Clock size={18} aria-hidden="true" />
            {t.footer.bookCall}
          </a>
          <a
            href={TALLY_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-sm font-semibold text-white/80 transition-colors hover:text-white"
            data-testid="footer-waitlist"
          >
            {t.footer.waitlist} →
          </a>
          <p className="text-xs text-white/40">{t.footer.copyright}</p>
        </div>
      </div>
    </footer>
  );
}

export default function App() {
  const { lang, setLang } = useLanguage();
  return (
    <>
      <Nav lang={lang} setLang={setLang} />
      <main>
        <Hero lang={lang} />
        <Process lang={lang} />
        <Proof lang={lang} />
        <UseCases lang={lang} />
        <Founders lang={lang} />
        <WallOfFame lang={lang} />
      </main>
      <Footer lang={lang} />
    </>
  );
}
